import React, { useRef, useEffect } from "react";

import { Box, Button, IconButton, Typography } from "@material-ui/core";
import { LinearProgress } from "@material-ui/core";
import { Dialog, useMediaQuery } from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";

import {
    createStyles,
    Theme,
    withStyles,
    WithStyles,
} from "@material-ui/core/styles";

import { useUpload } from "../hooks";

const styles = (theme: Theme) =>
    createStyles({
        root: {
            margin: 0,
            padding: theme.spacing(2),
            backgroundColor: "#f7f8f9",
        },
        closeButton: {
            position: "absolute",
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500],
        },
        fileName: {
            marginTop: 20,
        },
        success: {
            color: "green",
        },
        error: {
            color: "red",
        },
    });

const SuccessAlert = withStyles((theme: Theme) => ({
    root: {
        color: theme.palette.success.main,
    },
}))(Typography);

const ErrorAlert = withStyles((theme: Theme) => ({
    root: {
        color: theme.palette.error.main,
    },
}))(Typography);

const DialogContent = withStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(4),
    },
}))(MuiDialogContent);

const UploadButton = withStyles((theme: Theme) => ({
    root: {
        color: "#ffffff",
        fontWeight: "bold",
        marginTop: 10,
    },
}))(Button);

const DialogActions = withStyles((theme: Theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(3),
        justifyContent: "flex-start",
        display: "block",
    },
}))(MuiDialogActions);

export interface DialogTitleProps extends WithStyles<typeof styles> {
    id: string;
    children: React.ReactNode;
    onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});
export interface UploadModalProps {
    open: boolean;
    leadId: string;
    docType: string;
    prefix: string;
    description: string;
    closeHandler: () => void;
    setStatus: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({
    open,
    prefix,
    docType,
    leadId,
    description,
    closeHandler,
    setStatus,
}) => {
    const { file, setFile, status, upload } = useUpload(prefix, docType, leadId, open);
    const fullScreen = useMediaQuery("(max-width:600px)");
    const inputEl = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        (status.state === "success") && setStatus();
    }, [status, setStatus]);

    const changeHandler = (event: any) => {
        const selected = event.target.files[0];
        selected && setFile(selected);
    };

    const uploadHandler = () => upload();

    const close = () => {
        setFile(null);
        closeHandler();
    };

    const clickHandler = () => inputEl?.current?.click();

    return (
        <Dialog
            fullScreen={fullScreen}
            maxWidth="sm"
            fullWidth
            onClose={close}
            aria-labelledby={docType}
            open={open}
        >
            <DialogTitle id="upload-dialog-title" onClose={close}>
                {docType}
            </DialogTitle>
            <DialogContent>
                <Typography gutterBottom>{description}</Typography>
            </DialogContent>
            <DialogActions>
                <Box display={file?.name ? "none" : "block"}>
                    <UploadButton
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={clickHandler}
                    >
                        Choose File
                    </UploadButton>
                </Box>
                <Box display={file?.name ? "block" : "none"}>
                    <Typography style={{ margin: "10px 0px" }}>
                        {file?.name}
                    </Typography>
                </Box>
                <Box
                    display={
                        status.state === "start" || status.state === "request"
                            ? "block"
                            : "none"
                    }
                >
                    <UploadButton
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<CloudUploadIcon />}
                        onClick={uploadHandler}
                        disabled={status.state === "start"}
                    >
                        Upload
                    </UploadButton>
                </Box>

                <Box display={status.state === "uploading" ? "block" : "none"}>
                    <LinearProgress
                        variant="determinate"
                        value={status.progress}
                    />
                </Box>
                <Box display={status.state === "success" ? "block" : "none"}>
                    <SuccessAlert>File uploaded successfully.</SuccessAlert>
                    <UploadButton
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={close}
                    >
                        Close
                    </UploadButton>
                </Box>
                <Box display={status.state === "error" ? "block" : "none"}>
                    <ErrorAlert>{status.message}</ErrorAlert>
                </Box>
                <input
                    ref={inputEl}
                    id="file-input"
                    type="file"
                    name="name"
                    onChange={changeHandler}
                    accept=".jpeg,.jpg,.png,.pdf"
                    hidden
                />
            </DialogActions>
        </Dialog>
    );
};

export default UploadModal;
